import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Divider,
  CircularProgress,
  Chip,
  TextField as MuiTextField,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { formatDate } from "../../config/formatters.ts";
import { toast } from "react-toastify";

interface WashData {
  id: number;
  vehicle?: {
    regNo: string;
    customer?: { name: string; phone: string };
  };
  actualAmount?: number | null;
  payedAmount?: number | null;
  offerAmount?: number | null;
  isOfferApplied?: number | null;
  paymentReceivedOn?: string | null;
  washStatus?: string;
  paymentStatus?: string;
}

interface EditPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  wash: WashData | null;
  onSuccess: () => void;
}

export default function EditPaymentDialog({
  open,
  onClose,
  wash,
  onSuccess,
}: EditPaymentDialogProps) {
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const formik = useFormik({
    initialValues: {
      payments: [{ amount: "", accountId: "" }],
    },
    validationSchema: Yup.object({
      payments: Yup.array().of(
        Yup.object({
          amount: Yup.string().required("Amount is required"),
          accountId: Yup.string().required("Bank account is required"),
        }),
      ),
    }),
    onSubmit: async (values) => {
      if (!wash) return;
      try {
        await request.post("/api/updatePayment", {
          washId: wash.id,
          payments: values.payments.map((p) => ({
            amount: Number(p.amount),
            accountId: Number(p.accountId),
          })),
        });
        toast.success("Payment updated successfully!");
        onSuccess();
        onClose();
      } catch {
        toast.error("Failed to update payment");
      }
    },
  });

  const addPaymentRow = () => {
    formik.setFieldValue("payments", [
      ...formik.values.payments,
      { amount: "", accountId: "" },
    ]);
  };

  const removePaymentRow = (index: number) => {
    if (formik.values.payments.length <= 1) return;
    formik.setFieldValue(
      "payments",
      formik.values.payments.filter((_, i) => i !== index),
    );
  };

  const updatePaymentRow = (index: number, field: string, value: string) => {
    formik.setFieldValue(`payments[${index}].${field}`, value);
  };

  const getRowError = (index: number, field: string) => {
    const errors = formik.errors.payments as any;
    const touched = formik.touched.payments as any;
    return touched?.[index]?.[field] ? errors?.[index]?.[field] : undefined;
  };

  const offerAmount =
    wash?.isOfferApplied && wash?.offerAmount ? Number(wash.offerAmount) : 0;
  const amountDue = Math.max(Number(wash?.actualAmount || 0) - offerAmount, 0);
  const totalPayed = formik.values.payments.reduce(
    (sum, p) => sum + (Number(p.amount) || 0),
    0,
  );
  const remaining = amountDue - totalPayed;

  useEffect(() => {
    if (open && wash) {
      formik.resetForm();
      setLoaded(false);
      fetchAccounts();
      fetchPayments();
    }
  }, [open, wash]);

  const fetchAccounts = async () => {
    try {
      const res = await request.get("/api/staff/getActiveAccounts");
      if (res?.data?.accounts) {
        setAccounts(res.data.accounts);
      }
    } catch {
      setAccounts([]);
    }
  };

  const fetchPayments = async () => {
    if (!wash) return;
    setLoading(true);
    try {
      const res = await request.get("/api/getWashPayments", { washId: wash.id });
      const payments: any[] = res?.data?.data || [];
      if (payments.length) {
        formik.setFieldValue(
          "payments",
          payments.map((p) => ({
            amount: String(p.amount ?? ""),
            accountId: String(p.accountId ?? ""),
          })),
        );
      } else {
        formik.setFieldValue("payments", [
          {
            amount: String(wash.payedAmount ?? ""),
            accountId: "",
          },
        ]);
      }
    } catch {
      formik.setFieldValue("payments", [
        { amount: String(wash.payedAmount ?? ""), accountId: "" },
      ]);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  const accountOptions = accounts.map((acc) => ({
    id: acc.id,
    label: `${acc.accountName}${acc.accountNo ? ` (${acc.accountNo})` : ""} — ${acc.accountType}`,
  }));

  if (!wash) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, overflow: "hidden" } }}
    >
      <Box
        sx={{
          background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <EditIcon sx={{ fontSize: 22, color: "#fff" }} />
        <Box
          sx={{ flex: 1, color: "#fff", fontWeight: 700, fontSize: "0.95rem" }}
        >
          Edit Payment
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ color: "rgba(255,255,255,0.8)", p: 0.5 }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2.5 }}>
        {loading || !loaded ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              py: 4,
              fontSize: "0.8rem",
              color: "#64748b",
            }}
          >
            <CircularProgress size={16} />
            Loading payments...
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}
            >
              <Info label="Reg No" value={wash.vehicle?.regNo || "-"} />
              <Info
                label="Customer"
                value={wash.vehicle?.customer?.name || "-"}
              />
            </Box>

            <Divider sx={{ borderStyle: "dashed", opacity: 0.5 }} />

            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}
            >
              <Info
                label="Amount"
                value={
                  wash.actualAmount != null
                    ? `₹${Number(wash.actualAmount).toLocaleString()}`
                    : "-"
                }
              />
              <Info
                label="Paid On"
                value={
                  wash.paymentReceivedOn
                    ? formatDate(wash.paymentReceivedOn)
                    : "-"
                }
              />
            </Box>

            {offerAmount > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  color: "#b45309",
                  bgcolor: "rgba(245, 158, 11, 0.1)",
                  border: "1px solid rgba(245, 158, 11, 0.2)",
                  borderRadius: 1.5,
                  p: 1,
                }}
              >
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: 16 }}
                >
                  local_offer
                </span>
                Offer applied: -₹{offerAmount.toLocaleString()} · Amount due ₹
                {amountDue.toLocaleString()}
              </Box>
            )}

            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Box
                  sx={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: "#9ca3af",
                    textTransform: "uppercase",
                    letterSpacing: 0.3,
                  }}
                >
                  Payments
                </Box>
                <Button
                  size="small"
                  onClick={addPaymentRow}
                  startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.7rem",
                    color: "#1d4ed8",
                    px: 0.75,
                    py: 0.25,
                    minWidth: 0,
                    borderRadius: 1.5,
                  }}
                >
                  Add Row
                </Button>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {formik.values.payments.map((payment, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "150px 200px auto",
                      gap: 1,
                      alignItems: "start",
                      justifyContent: "start",
                    }}
                  >
                    <Box>
                      <MuiTextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Amount"
                        placeholder="0.00"
                        name={`payments[${index}].amount`}
                        value={payment.amount}
                        onChange={(e) =>
                          updatePaymentRow(index, "amount", e.target.value)
                        }
                        onBlur={formik.handleBlur}
                        error={Boolean(getRowError(index, "amount"))}
                        helperText={getRowError(index, "amount") as string}
                        sx={{ mt: 0, mb: 0 }}
                      />
                    </Box>

                    <Box>
                      <MuiTextField
                        fullWidth
                        select
                        size="small"
                        // label="Bank Account"
                        name={`payments[${index}].accountId`}
                        value={payment.accountId}
                        onChange={(e) =>
                          updatePaymentRow(index, "accountId", e.target.value)
                        }
                        shrink
                        onBlur={formik.handleBlur}
                        error={Boolean(getRowError(index, "accountId"))}
                        helperText={getRowError(index, "accountId") as string}
                        SelectProps={{ displayEmpty: true }}
                        sx={{ mt: 0, mb: 0 }}
                      >
                        <MenuItem value="" disabled>
                          Select account
                        </MenuItem>
                        {accountOptions.map((acc) => (
                          <MenuItem key={acc.id} value={String(acc.id)}>
                            {acc.label}
                          </MenuItem>
                        ))}
                      </MuiTextField>
                    </Box>

                    <IconButton
                      size="small"
                      onClick={() => removePaymentRow(index)}
                      disabled={formik.values.payments.length <= 1}
                      sx={{
                        mt: 1.5,
                        width: 32,
                        height: 32,
                        color: "#ef4444",
                        "&:disabled": { color: "#cbd5e1" },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>

              <Box
                sx={{
                  mt: 1,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 1,
                  borderRadius: 1.5,
                  bgcolor: "rgba(0,0,0,0.03)",
                }}
              >
                <Box
                  sx={{ fontSize: "0.72rem", fontWeight: 600, color: "#64748b" }}
                >
                  Total Payed
                </Box>
                <Box
                  sx={{
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    color:
                      remaining > 0
                        ? "#dc2626"
                        : totalPayed > 0
                        ? "#059669"
                        : "#64748b",
                  }}
                >
                  ₹{totalPayed.toFixed(2)}
                  {remaining !== 0 && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: "0.68rem",
                        fontWeight: 600,
                        color: remaining > 0 ? "#dc2626" : "#059669",
                        ml: 0.75,
                      }}
                    >
                      ({remaining > 0
                        ? `${remaining.toFixed(2)} due`
                        : `overpaid ${Math.abs(remaining).toFixed(2)}`}
                      )
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{
            borderColor: "rgba(0,0,0,0.12)",
            color: "#374151",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 1.5,
            px: 2,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => formik.handleSubmit()}
          variant="contained"
          size="small"
          disabled={formik.isSubmitting || loading || !loaded}
          startIcon={
            formik.isSubmitting ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <EditIcon />
            )
          }
          sx={{
            background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 1.5,
            px: 2,
            "&:hover": {
              background: "linear-gradient(135deg, #1d4ed8, #1e40af)",
            },
          }}
        >
          {formik.isSubmitting ? "Updating..." : "Update Payment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Box
        sx={{
          fontSize: "0.65rem",
          fontWeight: 700,
          color: "#9ca3af",
          textTransform: "uppercase",
          letterSpacing: 0.3,
          mb: 0.15,
        }}
      >
        {label}
      </Box>
      <Box
        sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#1c1b1f" }}
      >
        {value}
      </Box>
    </Box>
  );
}
